import { config } from '../../config';
import { POSITIONS } from '../../types';
import type { OurPlayerRow, Position } from '../../types';
import { ok, reject } from '../result';
import type {
  Decision,
  DeleteOurPlayerCommand,
  DeleteOurPlayerSnapshot,
  RosterOp,
  UpsertOurPlayerCommand,
  UpsertOurPlayerSnapshot,
} from './types';

/**
 * B spec §4.2, scenario 2 — **our own roster** (build unit B-2): the My Team list Coach Claude
 * reads (§5.2).
 *
 * ── Why these are commands and not repo calls from a screen ──────────────────
 *
 * The same answer `commands/setup.ts` gives for teams and players: `dispatch` is the only
 * mutation path, and `ourPlayers` is domain data — carried in archives (replace-all import, like
 * `teams`) and read into the Coach Claude payload. A second window that has gone read-only under
 * §13.7 must not write it from a screen.
 *
 * ── What is deliberately absent ──────────────────────────────────────────────
 *
 * **No state gate.** The roster is profile-scoped with no match owner (B-1's cascade-negative
 * test pins that no cascade reaches it), so there is no `Match.state` for a window to read —
 * spec §4.2's window column says "any", and a `matches` read here would be a table in the scope
 * for a fact nothing consults.
 *
 * **No content rule on the ratings.** `ratings` is a sparse map over a closed key space with a
 * closed value space, so the compiler and the repo-boundary schema between them leave the handler
 * nothing to check: there is no "empty" rating to refuse, because absence *is* the unrated state
 * (§3.7.37) and it is legal on every attribute.
 *
 * **What the handler DOES check is §3.7.36's identity and its execution filter**, because neither
 * is expressible in the type: a blank first name is a `string`, and a `secondaryPositions` array
 * that repeats the primary is a `Position[]`.
 */

const MODULE = 'commands/roster';

const POSITION_SET: ReadonlySet<Position> = new Set<Position>(POSITIONS);

/**
 * §3.7.36's position rules, as one verdict.
 *
 * @returns the refusal sentence, or `null` when the positions are legal.
 */
function positionProblem(row: OurPlayerRow): string | null {
  if (!POSITION_SET.has(row.primaryPosition)) {
    return `“${row.primaryPosition}” is not one of the positions this app knows.`;
  }
  const seen = new Set<Position>();
  for (const position of row.secondaryPositions) {
    if (!POSITION_SET.has(position)) {
      return `“${position}” is not one of the positions this app knows.`;
    }
    if (position === row.primaryPosition) {
      return 'A player’s main position is already theirs — it does not belong in “can also play”.';
    }
    if (seen.has(position)) {
      return `“${position}” is listed twice in “can also play”.`;
    }
    seen.add(position);
  }
  return null;
}

/**
 * B §4.2 — create or edit one roster player, decided against the in-transaction read.
 *
 * | `existing` | Outcome |
 * |---|---|
 * | none | insert the caller's row as built |
 * | the same id | update the editable fields; `createdAt` keeps its stored value |
 *
 * The edit arm narrows to the `updateOurPlayer` op's key union, so a stale caller cannot re-date
 * the row and the repo stamps `updatedAt` from the bus's clock.
 *
 * **Three refusals, and the order is deliberate**: identity first (§3.7.36 requires a number and a
 * name), then the number's band, then the positions. `ERR_SHIRT_NUMBER_RANGE` is **reused, not
 * re-minted** — it is the same 1–99 rule `commands/setup.ts` applies to opponent stubs, carrying
 * the same sentence, and a second code for the same band would be two names for one fact.
 */
export function handleUpsertOurPlayer(
  cmd: UpsertOurPlayerCommand,
  snap: UpsertOurPlayerSnapshot,
): Decision<RosterOp> {
  const { row } = cmd;

  if (row.firstName.trim() === '' || !Number.isInteger(row.jerseyNumber)) {
    return reject(
      MODULE,
      null,
      'ERR_ROSTER_IDENTITY_REQUIRED',
      'Give the player a shirt number and a first name so you can find them again.',
      { id: row.id },
    );
  }

  if (
    row.jerseyNumber < config.SHIRT_NUMBER_MIN ||
    row.jerseyNumber > config.SHIRT_NUMBER_MAX
  ) {
    return reject(
      MODULE,
      null,
      'ERR_SHIRT_NUMBER_RANGE',
      `A shirt number must be between ${String(config.SHIRT_NUMBER_MIN)} and ${String(config.SHIRT_NUMBER_MAX)}.`,
      { id: row.id, jerseyNumber: row.jerseyNumber },
    );
  }

  const positions = positionProblem(row);
  if (positions !== null) {
    return reject(MODULE, null, 'ERR_ROSTER_POSITION_INVALID', positions, { id: row.id });
  }

  if (snap.existing !== undefined) {
    return ok({
      ops: [
        {
          t: 'updateOurPlayer',
          id: row.id,
          changes: {
            jerseyNumber: row.jerseyNumber,
            firstName: row.firstName,
            primaryPosition: row.primaryPosition,
            secondaryPositions: row.secondaryPositions,
            ratings: row.ratings,
            active: row.active,
          },
        },
      ],
      log: [
        {
          module: MODULE,
          level: 'info',
          matchId: null,
          msg: 'roster player updated',
          data: { id: row.id, active: row.active },
        },
      ],
      post: [],
    });
  }

  return ok({
    ops: [{ t: 'insertOurPlayer', row }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: null,
        msg: 'roster player created',
        data: { id: row.id, active: row.active },
      },
    ],
    post: [],
  });
}

/**
 * B §4.2 — remove one roster player.
 *
 * A missing row is answered as a value (`ERR_ROSTER_NOT_FOUND`), not treated as success: the
 * roster is editable from any session, so a stale screen deleting an already-deleted row is an
 * ordinary event that deserves an ordinary sentence — the `alreadyResolved` argument from
 * `handleResolveFlag`, without the unique-index backstop because a `delete` of a missing key is
 * silently idempotent in IndexedDB and would otherwise report success for a no-op.
 */
export function handleDeleteOurPlayer(
  cmd: DeleteOurPlayerCommand,
  snap: DeleteOurPlayerSnapshot,
): Decision<RosterOp> {
  if (snap.existing === undefined) {
    return reject(
      MODULE,
      null,
      'ERR_ROSTER_NOT_FOUND',
      'That player is no longer on the roster.',
      { id: cmd.playerId },
    );
  }

  return ok({
    ops: [{ t: 'deleteOurPlayer', id: cmd.playerId }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: null,
        msg: 'roster player deleted',
        data: { id: cmd.playerId },
      },
    ],
    post: [],
  });
}
